
import { useEffect } from 'react';
import { trackEvent } from '../integrations/gtm';

interface UsePageTitleOptions {
  description?: string;
}

/**
 * Custom hook to set the page title and meta description for SEO
 * Now with special care not to interfere with MetaTags component
 * @param title - The title to set for the page
 * @param options - Optional options to set for the page
 */
const usePageTitle = (title: string, options: UsePageTitleOptions = {}) => {
  useEffect(() => {
    // Check if there's already a MetaTags component active by looking for OpenGraph tags
    const hasMetaTagsComponent = !!document.querySelector('meta[property="og:title"]');
    
    // Only set the title if there's no MetaTags component or preserve existing meta tags
    if (!hasMetaTagsComponent) {
      // Set the document title but don't touch meta tags if they exist
      document.title = title;
      
      // Only set description if there's no MetaTags component and no existing tag
      if (options.description) {
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', options.description);
      }
      
      // Track page view but explicitly tell it not to modify meta tags
      trackEvent('page_view', { 
        title: title,
        path: window.location.pathname,
        preserveMetaTags: true
      });
    }

    return () => {
      // No cleanup needed - next page will handle its own meta tags
    };
  }, [title, options.description]);
};

/**
 * Helper function to update or create meta tags
 * Now with special care to preserve existing values in certain cases
 */
const updateOrCreateMetaTag = (property: string, content: string, preserveExisting: boolean = false): void => {
  // Check if meta tag already exists using appropriate selector
  const isOpenGraph = property.startsWith('og:') || property.startsWith('twitter:');
  const selector = isOpenGraph ? `meta[property="${property}"]` : `meta[name="${property}"]`;
  
  let metaTag = document.querySelector(selector);
  
  // If we're preserving existing values and the tag exists, don't modify it
  if (preserveExisting && metaTag) {
    return;
  }
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    // Use property for OG and Twitter tags, name for others
    if (isOpenGraph) {
      metaTag.setAttribute('property', property);
    } else {
      metaTag.setAttribute('name', property);
    }
    document.head.appendChild(metaTag);
  }
  
  metaTag.setAttribute('content', content);
};

export default usePageTitle;
export type { UsePageTitleOptions };
export { updateOrCreateMetaTag };
